const lesson1_1 = {
  id: '1.1',

  chapterId: 1,
  lessonId: 1,

  title: 'Ce este contabilitatea primara',

  duration: 8,

  steps: [

    /* =====================================================
       PASUL 1
       INTRODUCERE
    ===================================================== */

    {
      id: 1,

      left: {
        type: 'theory',

        eyebrow: 'INTRODUCERE',

        title: 'Ce este contabilitatea primara?',

        content: [
         `
<p>
  📘 <b>Contabilitatea primara</b> reprezinta procesul prin care
  operatiunile economice ale unei firme sunt <b>documentate</b>,
  <b>verificate</b>, <b>organizate</b> si pregatite pentru evidenta contabila.
</p>

<p>
  In activitatea unei firme apar zilnic operatiuni precum:
</p>

<p>
  • cumpararea unor produse<br/>
  • vanzarea unor produse sau servicii<br/>
  • incasarea banilor de la clienti<br/>
  • plata furnizorilor<br/>
  • efectuarea unor operatiuni prin banca
</p>

<p>
  📄 Aceste operatiuni trebuie sustinute de <b>documente</b>.
</p>
`,
        ],

        highlight: {
          title: 'Ideea principala',

          text:
            'Contabilitatea primara transforma activitatea de zi cu zi a firmei in documente care pot fi verificate si urmarite.',
        },
      },

      right: {
        type: 'visualFlow',

        header: 'Cum functioneaza',

        title: 'De la activitatea firmei la evidenta',

        items: [
          {
            icon: '🏢',
            title: 'Activitatea firmei',
            description:
              'Firma cumpara, vinde, incaseaza sau plateste.',
          },
          {
            icon: '📄',
            title: 'Documente',
            description:
              'Operatiunile sunt sustinute de documente.',
          },
          {
            icon: '🔎',
            title: 'Verificare',
            description:
              'Informatiile din documente sunt controlate.',
          },
          {
            icon: '📂',
            title: 'Organizare',
            description:
              'Documentele sunt ordonate si pregatite.',
          },
          {
            icon: '📚',
            title: 'Evidenta contabila',
            description:
              'Informatiile pot fi utilizate in contabilitate.',
          },
        ],
      },
    },


    /* =====================================================
       PASUL 2
       PRIMA VERIFICARE
    ===================================================== */

    {
      id: 2,

      left: {
        type: 'scenario',

        eyebrow: 'SITUATIE',

        title: 'O operatiune din activitatea unei firme',

        content: [
          '<p>📦 NOVA SRL cumpara marfa de la un furnizor pentru a o utiliza in activitatea firmei.</p>',

          '<p>Operatiunea modifica situatia firmei si trebuie sustinuta printr-un <b>document justificativ</b>.</p>',
        ],
      },

      right: {
        type: 'simpleSelect',

        options: [
          'cumpararea marfii de la furnizor',
          'aranjarea fisierelor de pe calculator',
          'schimbarea imaginii de fundal',
        ],

        question:
          'Care situatie trebuie documentata in contabilitatea primara?',

        explanation:
          '<p>📦 <b>Cumpararea marfii de la furnizor</b> este o operatiune economica si trebuie sustinuta de documente.</p>',

        correctAnswer:
          'cumpararea marfii de la furnizor',
      },
    },


    /* =====================================================
       PASUL 3
       OPERATIUNE + DOCUMENT
    ===================================================== */

    {
      id: 3,

      left: {
        type: 'theory',

        eyebrow: 'DOCUMENTAREA OPERATIUNILOR',

        title: 'Operatiunea lasa o urma documentara',

        content: [
          '<p>🧾 O operatiune economica nu este urmarita doar prin ceea ce se intampla fizic in firma.</p>',

          '<p>📄 Ea este sustinuta de un <b>document justificativ</b> care arata ce s-a intamplat, intre cine si in ce conditii.</p>',

          '<p>🔗 Astfel apare legatura dintre <b>operatiune</b> si <b>document</b>.</p>',
        ],
      },

      right: {
        type: 'visualFlow',

        header: 'Legatura esentiala',

        title: 'Operatiune → document',

        items: [
          {
            icon: '📦',
            title: 'Cumparare de bunuri',
            description:
              'Firma primeste bunurile achizitionate.',
          },
          {
            icon: '➡️',
            title: 'Documentare',
            description:
              'Operatiunea trebuie sustinuta prin documente.',
          },
          {
            icon: '📄',
            title: 'Document justificativ',
            description:
              'Documentul permite verificarea operatiunii.',
          },
        ],
      },
    },


    /* =====================================================
       PASUL 4
       MULTIPLE SELECT
    ===================================================== */

    {
      id: 4,

      left: {
        type: 'theory',

        eyebrow: 'ACTIVITATI',

        title: 'Ce presupune contabilitatea primara?',

        content: [
          '<p>🔎 Documentele trebuie mai intai <b>verificate</b> pentru a observa daca informatiile sunt complete si corecte.</p>',

          '<p>📂 Apoi sunt <b>organizate</b> astfel incat sa poata fi urmarite si utilizate usor.</p>',

          '<p>📚 In final, acestea sunt pregatite pentru utilizarea in evidenta contabila.</p>',
        ],
      },

      right: {
        type: 'multipleSelect',

        options: [
          'verificarea documentelor primite',
          'organizarea documentelor firmei',
          'pregatirea documentelor pentru evidenta',
          'stabilirea campaniilor de promovare',
        ],

        question:
          'Care activitati fac parte din contabilitatea primara?',

        explanation:
          '<p>📚 Contabilitatea primara presupune <b>verificarea</b>, <b>organizarea</b> si <b>pregatirea documentelor</b> pentru evidenta.</p>',

        correctAnswer: [
          'verificarea documentelor primite',
          'organizarea documentelor firmei',
          'pregatirea documentelor pentru evidenta',
        ],
      },
    },


    /* =====================================================
       PASUL 5
       EXEMPLU NOVA SRL
    ===================================================== */

    {
      id: 5,

      left: {
        type: 'scenario',

        eyebrow: 'EXEMPLU PRACTIC',

        title: 'NOVA SRL primeste un document',

        content: [
          '<p>🏢 NOVA SRL cumpara produse de la un furnizor.</p>',

          '<p>📄 Odata cu bunurile, firma primeste si documentele aferente operatiunii.</p>',

          '<p>🔎 Persoana responsabila de contabilitatea primara trebuie sa verifice documentele inainte ca acestea sa fie organizate si transmise mai departe.</p>',
        ],
      },

      right: {
        type: 'simpleSelect',

        options: [
          'sa verifice informatiile documentului',
          'sa arhiveze imediat documentul primit',
          'sa elimine documentul dupa receptie',
        ],

        question:
          'Care este actiunea potrivita dupa primirea documentului?',

        explanation:
          '<p>🔎 Documentul trebuie mai intai <b>verificat</b> pentru a observa daca informatiile sunt corecte si complete.</p>',

        correctAnswer:
          'sa verifice informatiile documentului',
      },
    },


    /* =====================================================
       PASUL 6
       ADEVARAT / FALS
    ===================================================== */

    {
      id: 6,

      left: {
        type: 'theory',

        eyebrow: 'ROL',

        title: 'Contabilitatea primara nu inseamna doar arhivare',

        content: [
          '<p>📂 Pastrarea documentelor este importanta, dar contabilitatea primara nu se rezuma la depozitarea lor.</p>',

          '<p>🧠 Documentele trebuie <b>intelese</b>, <b>verificate</b>, <b>clasificate</b> si pregatite pentru utilizare.</p>',
        ],
      },

      right: {
        type: 'trueFalseQuiz',

        question:
          'Contabilitatea primara inseamna doar pastrarea documentelor intr-o arhiva.',

        explanation:
          '<p>📂 Contabilitatea primara include <b>verificarea</b>, <b>organizarea</b> si <b>pregatirea documentelor</b>, nu doar arhivarea lor.</p>',

        correctAnswer: 'Fals',
      },
    },


    /* =====================================================
       PASUL 7
       ORDINE LOGICA
    ===================================================== */

    {
      id: 7,

      left: {
        type: 'theory',

        eyebrow: 'FLUX',

        title: 'Documentele urmeaza un traseu logic',

        content: [
          '<p>🔄 In contabilitatea primara, documentele parcurg mai multe etape.</p>',

          '<p>Mai intai apare <b>operatiunea economica</b>, apoi documentul este primit sau intocmit, verificat si organizat.</p>',
        ],
      },

      right: {
        rows: [
          {
            slots: 4,
          },
        ],

        type: 'orderSelectionDrag',

        options: [
          'organizarea documentului',
          'aparitia operatiunii',
          'verificarea documentului',
          'primirea documentului',
        ],

        question:
          'Pune etapele in ordinea logica:',

        explanation:
          '<p>🔄 Fluxul logic este: <b>aparitia operatiunii</b>, <b>primirea documentului</b>, <b>verificarea</b> si apoi <b>organizarea</b>.</p>',

        correctAnswer: [
          'aparitia operatiunii',
          'primirea documentului',
          'verificarea documentului',
          'organizarea documentului',
        ],
      },
    },


    /* =====================================================
       PASUL 8
       APLICAREA CONCEPTULUI
    ===================================================== */

    {
      id: 8,

      left: {
        type: 'scenario',

        eyebrow: 'APLICA',

        title: 'Ai primit documentele zilei',

        content: [
          '<p>📥 La finalul zilei, NOVA SRL are mai multe documente rezultate din activitatea firmei.</p>',

          '<p>Unele provin din achizitii, altele din vanzari, incasari sau plati.</p>',

          '<p>Rolul contabilitatii primare este sa asigure un <b>circuit clar si verificabil</b> al acestor documente.</p>',
        ],
      },

      right: {
        type: 'simpleSelect',

        options: [
          'verificarea si organizarea documentelor',
          'modificarea informatiilor fara justificare',
          'eliminarea documentelor dupa utilizare',
        ],

        question:
          'Care actiune sustine un circuit corect al documentelor?',

        explanation:
          '<p>📂 Un circuit corect presupune <b>verificarea si organizarea documentelor</b> astfel incat acestea sa poata fi urmarite.</p>',

        correctAnswer:
          'verificarea si organizarea documentelor',
      },
    },


    /* =====================================================
       PASUL 9
       RECAPITULARE
    ===================================================== */

    {
      id: 9,

      left: {
        type: 'summary',

        eyebrow: 'RECAPITULARE',

        title: 'Ce ai invatat pana acum',

        content: [
          '<p>✅ Activitatea firmei genereaza <b>operatiuni economice</b>.</p>',

          '<p>✅ Operatiunile sunt sustinute prin <b>documente justificative</b>.</p>',

          '<p>✅ Documentele sunt <b>verificate</b> si <b>organizate</b>.</p>',

          '<p>✅ Informatiile pot fi apoi utilizate in <b>evidenta contabila</b>.</p>',
        ],
      },

      right: {
        type: 'dragAndDropHorizontal',

        sentenceParts: [
          'Contabilitatea primara porneste de la',
          '__SLOT__',
          'care sustin operatiunile firmei.',
        ],

        options: [
          {
            code: 'documente justificative',
          },
          {
            code: 'planuri comerciale',
          },
          {
            code: 'materiale publicitare',
          },
        ],

        explanation:
          '<p>📄 Contabilitatea primara porneste de la <b>documentele justificative</b> care sustin operatiunile realizate de firma.</p>',

        correctAnswer: [
          'documente justificative',
        ],
      },
    },


    /* =====================================================
       PASUL 10
       FINALUL LECTIEI
    ===================================================== */

    {
      id: 10,

      left: {
        type: 'summary',

        eyebrow: 'LECTIE FINALIZATA',

        title: 'Ai inteles baza contabilitatii primare',

        content: [
          '<p>🎯 Contabilitatea primara creeaza legatura dintre <b>activitatea reala a firmei</b> si documentele care o sustin.</p>',

          '<p>📄 In urmatoarele lectii vei invata cum functioneaza aceste documente, ce informatii trebuie sa contina si cum circula intr-o firma.</p>',
        ],

        highlight: {
          title: 'Retine',

          text:
            'Operatiune economica → document justificativ → verificare → organizare → evidenta.',
        },
      },

      right: {
        type: 'simpleSelect',

        options: [
          'transforma operatiunile firmei in informatii documentate',
          'stabileste strategia comerciala folosita de firma',
          'organizeaza activitatea promotionala a firmei',
        ],

        question:
          'Care afirmatie descrie cel mai bine rolul contabilitatii primare?',

        explanation:
          '<p>🎯 Contabilitatea primara <b>documenteaza si organizeaza operatiunile firmei</b> pentru ca acestea sa poata fi verificate si utilizate in evidenta contabila.</p>',

        correctAnswer:
          'transforma operatiunile firmei in informatii documentate',
      },
    },
  ],
};

export default lesson1_1;